import { useCallback, useEffect, useState } from 'react'
import { Zap, TrendingUp, Users, CheckCircle2, AlertTriangle, Share2, Users2, ArrowRight, Sparkles, Flame, Medal, Trophy, Award, X, Shield, Lightbulb, DollarSign, Info } from 'lucide-react'
import { Link, NavLink } from 'react-router-dom'
import confetti from 'canvas-confetti'
import { LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer } from 'recharts'
import { getRiskFlagCopy } from '@duva/shared'
import { api, AccountabilityPair, type Invite, type MeProfile } from '../lib/api'
import { resolveBuddyCheckInAction } from '../lib/buddyCheckIn'
import { useAuthStore } from '../store/authStore'
import { UpgradeModal, useUpgradeGuard } from '../components/UpgradeModal'
import { EngagementScoreExplainer } from '../components/EngagementScoreExplainer'
import { StreakShield } from '../components/StreakShield'
import clsx from 'clsx'

const INK = '#1a3a40'
const BRAND_TEAL = '#2A7A8A'
const BRAND_SAGE = '#B8C7B1'
const WARM_CARD = '#FDFCF8'
const WARM_BORDER = '#e0d9cc'

const CHECKLIST_DISMISSED_KEY = 'duva_setup_checklist_dismissed'
const ONBOARDING_DONE_KEY = 'duva_onboarding_done'
const CELEBRATION_FIRED_KEY = 'duva_first_celebration_fired'
const TZ_PROMPT_DISMISSED_KEY = 'duva_tz_prompt_dismissed'

function hasPendingInvite(invites: Invite[]): boolean {
  const now = Date.now()
  return invites.some((i) => new Date(i.expires_at).getTime() > now)
}

interface Summary {
  engagementScore: { score: number; computed_at: string } | null
  riskFlags: string[]
  frictionScore: number
  weeklyStats: { sent: number; opened: number; acted: number } | null
  activeMemberCount: number
  isAnonymized: boolean
}

interface MabStat {
  category: string
  success_count: number
  total_count: number
  conversion_rate: number
}

interface EngagementPoint {
  computed_at: string
  score: number
}

interface PersonalNudgeProgress {
  streakDays: number
  completedThisWeek: number
  completedThisMonth: number
  snoozedThisWeek: number
  sentThisWeek: number
}

/** GET /api/analytics/roi — org-wide behavioral ROI for Home team section */
interface HomeOrgRoi {
  estimatedValue: number
  valuePerAction: number
  totalActions: number
  labels: { headline: string; subline: string }
}

export function HomePage() {
  const { user } = useAuthStore()
  const { upgradeError, setUpgradeError, withUpgradeGuard } = useUpgradeGuard()

  const [summary, setSummary] = useState<Summary | null>(null)
  const [trend, setTrend] = useState<EngagementPoint[]>([])
  const [mabStats, setMabStats] = useState<MabStat[]>([])
  const [loading, setLoading] = useState(true)
  const [sharing, setSharing] = useState(false)
  const [shareUrl, setShareUrl] = useState<string | null>(null)
  const [shareCopied, setShareCopied] = useState(false)

  const [myPair, setMyPair] = useState<AccountabilityPair | null>(null)
  const [buddyLoading, setBuddyLoading] = useState(true)

  const [goal, setGoal] = useState('')
  const [track, setTrack] = useState<string | null>(null)
  const [settingGoal, setSettingGoal] = useState(false)

  const [myProgress, setMyProgress] = useState<PersonalNudgeProgress | null>(null)
  const [progressLoading, setProgressLoading] = useState(true)

  const [insight, setInsight] = useState<{ insight: string; generatedAt: string } | null>(null)
  const [insightLoading, setInsightLoading] = useState(true)

  const [summaryError, setSummaryError] = useState<string | null>(null)
  const [trendError, setTrendError] = useState<string | null>(null)
  const [mabError, setMabError] = useState<string | null>(null)
  const [buddyError, setBuddyError] = useState<string | null>(null)
  const [progressError, setProgressError] = useState<string | null>(null)
  const [insightError, setInsightError] = useState<string | null>(null)

  const [orgChecklist, setOrgChecklist] = useState<{
    slack_workspace_id: string | null
    employee_count: number
  } | null>(null)
  const [invitesForChecklist, setInvitesForChecklist] = useState<Invite[]>([])
  /** undefined = still loading; null/string = loaded (from getMe) */
  const [meTimezone, setMeTimezone] = useState<string | null | undefined>(undefined)
  /** Preferred channel + org Slack team id for buddy deep links */
  const [buddyMeContext, setBuddyMeContext] = useState<Pick<MeProfile, 'preferred_channel' | 'slack_team_id'> | null>(null)
  const [checklistDismissed, setChecklistDismissed] = useState(
    () => typeof localStorage !== 'undefined' && localStorage.getItem(CHECKLIST_DISMISSED_KEY) === '1'
  )

  const [deliveryHealth, setDeliveryHealth] = useState<{
    deliveriesLast24h: number
    deliveriesLast7d: number
    deliveriesLast30d: number
    lastDeliverySentAt: string | null
    usersWithNudgesOn: number
    usersEligibleNoDeliveryTodayUtc: number
    approvedNudgeTemplates: number
    schedulerNote: string
  } | null>(null)
  const [deliveryHealthLoading, setDeliveryHealthLoading] = useState(false)
  const [deliveryHealthError, setDeliveryHealthError] = useState<string | null>(null)

  // Streak shield (KV-backed, no DB migration)
  const [shieldAvailable, setShieldAvailable] = useState<boolean | null>(null)
  const [shieldUsed, setShieldUsed] = useState(false)

  // Track examples for goal→track explainer
  const [trackExamples, setTrackExamples] = useState<string[]>([])

  const [homeRoi, setHomeRoi] = useState<HomeOrgRoi | null>(null)
  const [homeRoiLoading, setHomeRoiLoading] = useState(false)

  const [homePulse, setHomePulse] = useState<{
    stats: {
      inspiring: number
      developing: number
      planning: number
      vision: number
      change: number
      response_count: number
    } | null
    isAnonymized: boolean
    count?: number
    monthLabel?: string
  } | null>(null)
  const [homePulseLoading, setHomePulseLoading] = useState(false)

  // First nudge celebration (fires once)
  const [showCelebration, setShowCelebration] = useState(false)

  // Employee onboarding modal
  const [showOnboarding, setShowOnboarding] = useState(false)
  const [onboardingStep, setOnboardingStep] = useState(0)

  // Cloudflare-detected timezone from getMe
  const [detectedTimezone, setDetectedTimezone] = useState<string | null>(null)
  const [savingTz, setSavingTz] = useState(false)
  const [dismissedTzPrompt, setDismissedTzPrompt] = useState(
    () => typeof localStorage !== 'undefined' && localStorage.getItem(TZ_PROMPT_DISMISSED_KEY) === '1'
  )

  const dismissChecklist = useCallback(() => {
    localStorage.setItem(CHECKLIST_DISMISSED_KEY, '1')
    setChecklistDismissed(true)
  }, [])

  useEffect(() => {
    let cancelled = false
    const ok = () => !cancelled

    setLoading(true)
    setSummaryError(null)
    api
      .getSummary()
      .then((s) => {
        if (ok()) {
          setSummary(s as Summary)
          setSummaryError(null)
        }
      })
      .catch(() => {
        if (ok()) {
          setSummary(null)
          setSummaryError('Couldn’t load dashboard summary.')
        }
      })
      .finally(() => {
        if (ok()) setLoading(false)
      })

    setTrendError(null)
    api
      .getEngagement(30)
      .then((e) => {
        if (ok()) {
          setTrend((e as { scores: EngagementPoint[] }).scores ?? [])
          setTrendError(null)
        }
      })
      .catch(() => {
        if (ok()) {
          setTrend([])
          setTrendError('Couldn’t load engagement trend.')
        }
      })

    setMabError(null)
    api
      .getMabStats()
      .then((m) => {
        if (ok()) {
          setMabStats((m as { stats: MabStat[] }).stats ?? [])
          setMabError(null)
        }
      })
      .catch(() => {
        if (ok()) {
          setMabStats([])
          setMabError('Couldn’t load track effectiveness.')
        }
      })

    setBuddyError(null)
    setBuddyLoading(true)
    api
      .getAccountabilityPairs()
      .then((res) => {
        if (!ok()) return
        const pairs = (res as { pairs: AccountabilityPair[] }).pairs ?? []
        const mine = pairs.find((p) => p.user_a_id === user?.id || p.user_b_id === user?.id)
        setMyPair(mine ?? null)
        setBuddyError(null)
      })
      .catch(() => {
        if (ok()) {
          setMyPair(null)
          setBuddyError('Couldn’t load buddy info.')
        }
      })
      .finally(() => {
        if (ok()) setBuddyLoading(false)
      })

    api
      .getGoal()
      .then((res) => {
        if (ok() && res) {
          setGoal(res.goal)
          setTrack(res.track)
        }
      })
      .catch(() => {
        /* optional goal */
      })

    setProgressError(null)
    setProgressLoading(true)
    api
      .getNudgeProgress()
      .then((p) => {
        if (ok()) {
          setMyProgress(p)
          setProgressError(null)
          // 🎉 First nudge celebration — fires exactly once
           if (
            p.streakDays === 1 &&
            p.completedThisWeek === 1 &&
            typeof localStorage !== 'undefined' &&
            !localStorage.getItem(CELEBRATION_FIRED_KEY)
          ) {
            setShowCelebration(true)
            localStorage.setItem(CELEBRATION_FIRED_KEY, '1')
            confetti({
              particleCount: 150,
              spread: 70,
              origin: { y: 0.6 },
              colors: ['#2A7A8A', '#B8C7B1', '#FFD700']
            })
          }
        }
      })
      .catch(() => {
        if (ok()) {
          setMyProgress(null)
          setProgressError('Couldn’t load your nudge activity.')
        }
      })
      .finally(() => {
        if (ok()) setProgressLoading(false)
      })

    api
      .getMe()
      .then((me) => {
        if (ok()) {
          setMeTimezone(me.timezone ?? null)
          setDetectedTimezone((me as { detected_timezone?: string | null }).detected_timezone ?? null)
          setBuddyMeContext({
            preferred_channel: me.preferred_channel,
            slack_team_id: me.slack_team_id ?? null,
          })
        }
      })
      .catch(() => {
        if (ok()) {
          setMeTimezone(null)
          setBuddyMeContext(null)
        }
      })

    api
      .getOrgSettings()
      .then((o) => {
        if (ok()) {
          setOrgChecklist({
            slack_workspace_id: o.slack_workspace_id,
            employee_count: o.employee_count,
          })
        }
      })
      .catch(() => {
        if (ok()) setOrgChecklist(null)
      })

    if (user?.role === 'admin') {
      setDeliveryHealthLoading(true)
      setDeliveryHealthError(null)
      api
        .getOrgDeliveryHealth()
        .then((h) => {
          if (ok()) {
            setDeliveryHealth(h)
            setDeliveryHealthError(null)
          }
        })
        .catch(() => {
          if (ok()) {
            setDeliveryHealth(null)
            setDeliveryHealthError('Couldn’t load delivery status.')
          }
        })
        .finally(() => {
          if (ok()) setDeliveryHealthLoading(false)
        })

      api
        .getInvites()
        .then((r) => {
          if (ok()) setInvitesForChecklist(r.invites)
        })
        .catch(() => {
          if (ok()) setInvitesForChecklist([])
        })

      setInsightLoading(true)
      setInsightError(null)
      api
        .getInsights()
        .then((res) => {
          if (ok()) {
            setInsight(res)
            setInsightError(null)
          }
        })
        .catch(() => {
          if (ok()) {
            setInsight(null)
            setInsightError('Couldn’t load executive insight.')
          }
        })
        .finally(() => {
          if (ok()) setInsightLoading(false)
        })
    } else {
      setInvitesForChecklist([])
      setInsight(null)
      setInsightLoading(false)
      setInsightError(null)
      setDeliveryHealth(null)
      setDeliveryHealthLoading(false)
      setDeliveryHealthError(null)
    }

    if (user) {
      setHomeRoiLoading(true)
      setHomeRoi(null)
      api
        .getRoi(30)
        .then((r) => {
          if (ok()) {
            setHomeRoi({
              estimatedValue: r.estimatedValue,
              valuePerAction: r.valuePerAction,
              totalActions: r.totalActions,
              labels: r.labels,
            })
          }
        })
        .catch(() => {
          if (ok()) setHomeRoi(null)
        })
        .finally(() => {
          if (ok()) setHomeRoiLoading(false)
        })
    } else {
      setHomeRoi(null)
      setHomeRoiLoading(false)
    }

    if (user) {
      setHomePulseLoading(true)
      setHomePulse(null)
      api
        .getPulseStats('month')
        .then((p) => {
          if (ok()) setHomePulse(p)
        })
        .catch(() => {
          if (ok()) setHomePulse(null)
        })
        .finally(() => {
          if (ok()) setHomePulseLoading(false)
        })
    } else {
      setHomePulse(null)
      setHomePulseLoading(false)
    }

    // Streak shield
    api.getStreakShield()
      .then(r => { if (ok()) { setShieldAvailable(r.available); setShieldUsed(r.usedThisMonth) } })
      .catch(() => {})

    // Employee onboarding modal (fires once, for non-admin on first visit)
    if (
      user?.role === 'employee' &&
      typeof localStorage !== 'undefined' &&
      !localStorage.getItem(ONBOARDING_DONE_KEY)
    ) {
      setShowOnboarding(true)
    }

    return () => {
      cancelled = true
    }
  }, [user?.id, user?.role])


  const retrySummary = useCallback(() => {
    setSummaryError(null)
    setLoading(true)
    api
      .getSummary()
      .then((s) => {
        setSummary(s as Summary)
        setSummaryError(null)
      })
      .catch(() => {
        setSummary(null)
        setSummaryError('Couldn’t load dashboard summary.')
      })
      .finally(() => setLoading(false))
  }, [])

  const retryTrend = useCallback(() => {
    setTrendError(null)
    api
      .getEngagement(30)
      .then((e) => {
        setTrend((e as { scores: EngagementPoint[] }).scores ?? [])
        setTrendError(null)
      })
      .catch(() => {
        setTrend([])
        setTrendError('Couldn’t load engagement trend.')
      })
  }, [])

  const retryMab = useCallback(() => {
    setMabError(null)
    api
      .getMabStats()
      .then((m) => {
        setMabStats((m as { stats: MabStat[] }).stats ?? [])
        setMabError(null)
      })
      .catch(() => {
        setMabStats([])
        setMabError('Couldn’t load track effectiveness.')
      })
  }, [])

  const retryBuddy = useCallback(() => {
    setBuddyError(null)
    setBuddyLoading(true)
    api
      .getAccountabilityPairs()
      .then((res) => {
        const pairs = (res as { pairs: AccountabilityPair[] }).pairs ?? []
        const mine = pairs.find((p) => p.user_a_id === user?.id || p.user_b_id === user?.id)
        setMyPair(mine ?? null)
        setBuddyError(null)
      })
      .catch(() => {
        setMyPair(null)
        setBuddyError('Couldn’t load buddy info.')
      })
      .finally(() => setBuddyLoading(false))
  }, [user?.id])

  const retryProgress = useCallback(() => {
    setProgressError(null)
    setProgressLoading(true)
    api
      .getNudgeProgress()
      .then((p) => {
        setMyProgress(p)
        setProgressError(null)
      })
      .catch(() => {
        setMyProgress(null)
        setProgressError('Couldn’t load your nudge activity.')
      })
      .finally(() => setProgressLoading(false))
  }, [])

  const retryInsight = useCallback(() => {
    setInsightError(null)
    setInsightLoading(true)
    api
      .getInsights()
      .then((res) => {
        setInsight(res)
        setInsightError(null)
      })
      .catch(() => {
        setInsight(null)
        setInsightError('Couldn’t load executive insight.')
      })
      .finally(() => setInsightLoading(false))
  }, [])

  async function handleShare() {
    setSharing(true)
    setShareCopied(false)
    try {
      const sessionTz =
        (meTimezone && String(meTimezone).trim()) ||
        (detectedTimezone && detectedTimezone.trim()) ||
        (typeof Intl !== 'undefined' ? Intl.DateTimeFormat().resolvedOptions().timeZone : undefined) ||
        undefined
      const res = await withUpgradeGuard(() => api.generateReport({ timezone: sessionTz }))
      if (res) setShareUrl((res as { shareUrl: string }).shareUrl)
    } finally {
      setSharing(false)
    }
  }

  async function copyShareLink() {
    if (!shareUrl) return
    try {
      await navigator.clipboard.writeText(shareUrl)
      setShareCopied(true)
      setTimeout(() => setShareCopied(false), 2500)
    } catch {
      /* ignore */
    }
  }

  async function handleSetGoal(e: React.FormEvent) {
    e.preventDefault()
    if (!goal.trim()) return
    setSettingGoal(true)
    try {
      const res = await api.postGoal(goal)
      setTrack(res.track)
      // Fetch 2 example nudges for the track explainer
      api.getTrackExamples(res.track).then(r => setTrackExamples(r.examples)).catch(() => {})
    } finally {
      setSettingGoal(false)
    }
  }

  // Load track examples when track is already set on mount
  useEffect(() => {
    if (track) {
      api.getTrackExamples(track).then(r => setTrackExamples(r.examples)).catch(() => {})
    }
  }, [track])

  async function acceptDetectedTz() {
    if (!detectedTimezone) return
    setSavingTz(true)
    try {
      await api.patchMe({ timezone: detectedTimezone })
      setMeTimezone(detectedTimezone)
    } finally {
      setSavingTz(false)
    }
  }

  function dismissTzPrompt() {
    if (typeof localStorage !== 'undefined') localStorage.setItem(TZ_PROMPT_DISMISSED_KEY, '1')
    setDismissedTzPrompt(true)
  }

  function dismissOnboarding() {
    if (typeof localStorage !== 'undefined') localStorage.setItem(ONBOARDING_DONE_KEY, '1')
    setShowOnboarding(false)
  }

  const ONBOARDING_STEPS = [
    {
      emoji: '🕊',
      title: 'Welcome to Duva',
      body: 'Duva sends you short, science-backed prompts via Slack to help you build better habits at work — one small action at a time.',
    },
    {
      emoji: '🎯',
      title: 'Set your goal',
      body: 'Tell Duva what you want to improve. Our AI tunes your nudges to focus on what matters most — leadership, collaboration, focus, or more.',
    },
    {
      emoji: '✅',
      title: 'Act, then track',
      body: 'When a nudge arrives in Slack, tap Done to log it. Build a streak, earn monthly badges, and watch your habits grow over time.',
    },
  ]

  const riskFlags = summary?.riskFlags ?? []

  const score = summary?.engagementScore?.score ?? null
  const scoreColor =
    score === null
      ? 'text-ink-muted'
      : score >= 70
        ? 'text-emerald-800'
        : score >= 40
          ? 'text-amber-800'
          : 'text-red-700'

  const chartData = trend.map(p => ({
    date: new Date(p.computed_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }),
    score: p.score,
  }))

  return (
    <div className="p-8 max-w-5xl mx-auto">
      {upgradeError && (
        <UpgradeModal error={upgradeError} onClose={() => setUpgradeError(null)} />
      )}

      {/* 🎉 First nudge celebration banner */}
      {showCelebration && (
        <div
          className="mb-6 rounded-2xl border p-5 flex items-start gap-4 shadow-card animate-fade-in"
          style={{ background: '#f0fdf4', borderColor: '#6ee7b7' }}
        >
          <div className="text-3xl shrink-0">🎉</div>
          <div className="flex-1">
            <p className="font-semibold text-green-900 text-sm">First nudge done — you've started a habit!</p>
            <p className="text-green-800 text-xs mt-1">That first tap is the most important one. Your journey starts today. Keep the streak going!</p>
          </div>
          <button type="button" onClick={() => setShowCelebration(false)} className="text-green-700 hover:text-green-900 shrink-0">
            <X size={16} />
          </button>
        </div>
      )}

      {/* 👋 Employee onboarding modal (3-step, shown once) */}
      {showOnboarding && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/30 backdrop-blur-sm">
          <div
            className="w-full max-w-sm rounded-3xl border p-8 shadow-2xl"
            style={{ background: '#FDFCF8', borderColor: '#e0d9cc' }}
          >
            <div className="text-center mb-6">
              <div className="text-5xl mb-4">{ONBOARDING_STEPS[onboardingStep].emoji}</div>
              <h2 className="text-xl font-bold text-ink" style={{ fontFamily: "'Playfair Display', Georgia, serif" }}>
                {ONBOARDING_STEPS[onboardingStep].title}
              </h2>
              <p className="text-sm text-ink-muted mt-2 leading-relaxed">
                {ONBOARDING_STEPS[onboardingStep].body}
              </p>
            </div>
            <div className="flex gap-1.5 justify-center mb-6">
              {ONBOARDING_STEPS.map((_, i) => (
                <div
                  key={i}
                  className="h-1.5 rounded-full transition-all"
                  style={{
                    width: i === onboardingStep ? '24px' : '8px',
                    background: i === onboardingStep ? '#2A7A8A' : '#e0d9cc',
                  }}
                />
              ))}
            </div>
            <div className="flex gap-2">
              {onboardingStep > 0 && (
                <button
                  type="button"
                  onClick={() => setOnboardingStep(s => s - 1)}
                  className="flex-1 py-2.5 rounded-full border border-warm-border text-sm text-ink-muted hover:bg-warm-inset transition-colors"
                >
                  Back
                </button>
              )}
              <button
                type="button"
                onClick={() => {
                  if (onboardingStep < ONBOARDING_STEPS.length - 1) {
                    setOnboardingStep(s => s + 1)
                  } else {
                    dismissOnboarding()
                  }
                }}
                className="flex-1 py-2.5 rounded-full text-white text-sm font-semibold shadow-glow-teal"
                style={{ background: '#2A7A8A' }}
              >
                {onboardingStep < ONBOARDING_STEPS.length - 1 ? 'Next →' : "Let's go! 🚀"}
              </button>
            </div>
            <button
              type="button"
              onClick={dismissOnboarding}
              className="block text-center text-xs text-ink-soft hover:text-ink w-full mt-3"
            >
              Skip intro
            </button>
          </div>
        </div>
      )}

      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-2xl font-bold text-ink" style={{fontFamily: "'Playfair Display', Georgia, serif"}}>
            Good {getTimeOfDay()}, {user?.name?.split(' ')[0]} 👋
          </h1>
          <p className="text-sm mt-1" style={{color: '#6b8a8f'}}>Here&apos;s how your team is doing</p>
        </div>
        <button
          onClick={handleShare}
          disabled={sharing}
          className="flex items-center gap-2 text-white font-medium text-sm px-5 py-2 rounded-full transition-colors disabled:opacity-50 shadow-glow-teal"
          style={{ background: '#2A7A8A' }}
          onMouseEnter={e => (e.currentTarget.style.background = '#236b7a')}
          onMouseLeave={e => (e.currentTarget.style.background = '#2A7A8A')}
        >
          <Share2 size={15} />
          {sharing ? 'Generating…' : 'Share Report'}
        </button>
      </div>

      {shareUrl && (
        <div
          className="mb-6 rounded-xl border px-4 py-4 space-y-3"
          style={{ background: WARM_CARD, borderColor: BRAND_TEAL, borderWidth: 1, boxShadow: '0 0 24px -8px rgba(42,122,138,0.25)' }}
        >
          <div className="flex items-start gap-3">
            <CheckCircle2 size={18} className="text-brand-teal shrink-0 mt-0.5" />
            <div className="min-w-0 flex-1">
              <p className="text-sm font-medium text-ink">Shareable report is ready</p>
              <p className="text-xs text-ink-muted mt-1 leading-relaxed">
                Opens a formatted HTML summary: engagement score, 30-day and weekly nudge activity, trend, risk signals, top nudges by action rate, and track effectiveness (MAB). Anyone with the link can view it—no login required.
              </p>
            </div>
          </div>
          <div className="flex flex-wrap items-center gap-2 pl-[30px]">
            <a
              href={shareUrl}
              target="_blank"
              rel="noreferrer"
              className="inline-flex items-center gap-1.5 text-sm font-medium text-white px-4 py-2 rounded-lg transition-colors shadow-glow-teal"
              style={{ background: BRAND_TEAL }}
            >
              Open report
              <ArrowRight size={14} />
            </a>
            <button
              type="button"
              onClick={() => void copyShareLink()}
              className="text-sm font-medium px-4 py-2 rounded-lg border border-warm-border bg-warm-card hover:bg-warm-inset text-ink transition-colors"
            >
              {shareCopied ? 'Copied!' : 'Copy link'}
            </button>
          </div>
          <p className="text-[11px] text-ink-faint font-mono break-all pl-[30px]">{shareUrl}</p>
        </div>
      )}

      {!checklistDismissed &&
        user &&
        ((user.role === 'admin' && orgChecklist) || (user.role !== 'admin' && meTimezone !== undefined)) && (
          <div
            className="mb-6 rounded-2xl border p-5 shadow-card relative"
            style={{ background: WARM_CARD, borderColor: BRAND_TEAL, borderWidth: 1 }}
          >
            <button
              type="button"
              onClick={dismissChecklist}
              className="absolute top-3 right-3 p-1 rounded-lg text-ink-soft hover:text-ink hover:bg-warm-inset transition-colors"
              aria-label="Dismiss setup checklist"
            >
              <X size={16} />
            </button>
            <h2 className="text-sm font-semibold text-ink mb-1 pr-8">Slack pilot checklist</h2>
            <p className="text-xs text-ink-muted mb-3 pr-8">
              Path to first value: connect → roster → invites → confirm sends → review Analytics.
            </p>
            {user.role === 'admin' && orgChecklist ? (
              (() => {
                const slackOk = Boolean(orgChecklist.slack_workspace_id)
                const teamOk = orgChecklist.employee_count >= 2
                const inviteOk =
                  hasPendingInvite(invitesForChecklist) || orgChecklist.employee_count >= 3
                const deliveriesOk =
                  !deliveryHealthLoading &&
                  deliveryHealth != null &&
                  (deliveryHealth.deliveriesLast7d > 0 || deliveryHealth.deliveriesLast30d > 0)
                const allDone = slackOk && teamOk && inviteOk && deliveriesOk
                if (allDone) {
                  return (
                    <div className="space-y-2 text-sm text-ink-muted">
                      <p>
                        Pilot setup complete.{' '}
                        <NavLink to="/dashboard/analytics" className="text-brand-teal font-medium hover:underline">
                          Open Analytics
                        </NavLink>{' '}
                        for team engagement, or{' '}
                        <button type="button" className="text-brand-teal font-medium underline underline-offset-2" onClick={dismissChecklist}>
                          dismiss
                        </button>
                        .
                      </p>
                    </div>
                  )
                }
                return (
                  <ul className="space-y-2 text-sm text-ink-muted">
                    {!slackOk && (
                      <li className="flex flex-wrap items-center gap-2">
                        <span className="text-amber-700">○</span>
                        Connect Slack in{' '}
                        <NavLink to="/dashboard/settings" className="text-brand-teal font-medium hover:underline">
                          Settings
                        </NavLink>
                      </li>
                    )}
                    {!teamOk && (
                      <li className="flex flex-wrap items-center gap-2">
                        <span className="text-amber-700">○</span>
                        Sync your team roster (2+ people) in{' '}
                        <NavLink to="/dashboard/settings" className="text-brand-teal font-medium hover:underline">
                          Settings
                        </NavLink>
                      </li>
                    )}
                    {!inviteOk && (
                      <li className="flex flex-wrap items-center gap-2">
                        <span className="text-amber-700">○</span>
                        Invite teammates or reach 3+ members via{' '}
                        <NavLink to="/dashboard/settings" className="text-brand-teal font-medium hover:underline">
                          Settings
                        </NavLink>
                      </li>
                    )}
                    {slackOk && teamOk && inviteOk && (
                      <li className="flex flex-wrap items-start gap-2">
                        <span className="text-amber-700 shrink-0">{deliveriesOk ? '●' : '○'}</span>
                        <span>
                          {deliveryHealthLoading
                            ? 'Checking whether nudges have been delivered…'
                            : deliveriesOk
                              ? 'Nudges are reaching the team (seen in the last 7–30 days).'
                              : 'Confirm nudges are sending — see “Nudge delivery status” below, then check member timezones and that nudges are on.'}
                        </span>
                      </li>
                    )}
                  </ul>
                )
              })()
            ) : (
              (() => {
                const tzOk = Boolean(meTimezone && String(meTimezone).trim())
                const goalOk = Boolean(track) || goal.trim().length > 0
                const allDone = tzOk && goalOk
                if (allDone) {
                  return (
                    <p className="text-sm text-ink-muted">
                      You&apos;re all set.{' '}
                      <button type="button" className="text-brand-teal font-medium underline underline-offset-2" onClick={dismissChecklist}>
                        Dismiss
                      </button>
                    </p>
                  )
                }
                return (
                  <ul className="space-y-2 text-sm text-ink-muted">
                    {!tzOk && (
                      <li className="flex flex-wrap items-center gap-2">
                        <span className="text-amber-700">○</span>
                        Set your timezone in{' '}
                        <NavLink to="/dashboard/settings" className="text-brand-teal font-medium hover:underline">
                          Settings
                        </NavLink>
                      </li>
                    )}
                    {!goalOk && (
                      <li className="flex flex-wrap items-center gap-2">
                        <span className="text-amber-700">○</span>
                        Set a personal goal in{' '}
                        <a href="#personal-goal" className="text-brand-teal font-medium hover:underline">
                          Personal AI Coach
                        </a>{' '}
                        below
                      </li>
                    )}
                  </ul>
                )
              })()
            )}
          </div>
        )}

      {user?.role === 'admin' && (
        <div
          className="mb-6 rounded-2xl border p-5 shadow-card"
          style={{ background: WARM_CARD, borderColor: WARM_BORDER }}
        >
          <h2 className="text-sm font-semibold text-ink mb-2">Nudge delivery status</h2>
          {deliveryHealthError && (
            <p className="text-xs text-red-700 mb-2">{deliveryHealthError}</p>
          )}
          {deliveryHealthLoading && !deliveryHealth ? (
            <p className="text-xs text-ink-muted">Loading…</p>
          ) : deliveryHealth ? (
            <div className="space-y-3">
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 text-xs">
                <div className="bg-warm-inset/80 rounded-lg px-3 py-2 border border-warm-border">
                  <p className="text-ink-muted">Last 7 days</p>
                  <p className="text-lg font-semibold text-ink">{deliveryHealth.deliveriesLast7d}</p>
                  <p className="text-[10px] text-ink-muted">deliveries</p>
                </div>
                <div className="bg-warm-inset/80 rounded-lg px-3 py-2 border border-warm-border">
                  <p className="text-ink-muted">Last 24h</p>
                  <p className="text-lg font-semibold text-ink">{deliveryHealth.deliveriesLast24h}</p>
                </div>
                <div className="bg-warm-inset/80 rounded-lg px-3 py-2 border border-warm-border">
                  <p className="text-ink-muted">Members nudges on</p>
                  <p className="text-lg font-semibold text-ink">{deliveryHealth.usersWithNudgesOn}</p>
                </div>
                <div className="bg-warm-inset/80 rounded-lg px-3 py-2 border border-warm-border">
                  <p className="text-ink-muted">Last send</p>
                  <p className="text-sm font-medium text-ink truncate" title={deliveryHealth.lastDeliverySentAt ?? ''}>
                    {deliveryHealth.lastDeliverySentAt
                      ? new Date(deliveryHealth.lastDeliverySentAt).toLocaleString(undefined, {
                          dateStyle: 'medium',
                          timeStyle: 'short',
                        })
                      : '—'}
                  </p>
                </div>
              </div>
              <p className="text-[11px] text-ink-muted leading-relaxed">{deliveryHealth.schedulerNote}</p>
              <p className="text-[11px] text-ink-muted">
                Templates approved (global library): {deliveryHealth.approvedNudgeTemplates}. Members due today (UTC) without a send yet:{' '}
                {deliveryHealth.usersEligibleNoDeliveryTodayUtc}.
              </p>
            </div>
          ) : null}
        </div>
      )}

      {user?.role === 'admin' && (
        <div
          className="mb-8 rounded-2xl border p-6 relative overflow-hidden shadow-card animate-fade-in"
          style={{ background: WARM_CARD, borderColor: WARM_BORDER }}
        >
          <div className="absolute top-0 right-0 p-3 opacity-10 pointer-events-none">
            <Sparkles size={80} className="text-brand-teal" />
          </div>
          <div className="relative">
            <div className="flex items-center gap-2 mb-3">
              <div className="bg-brand-teal/10 p-1.5 rounded-lg">
                <Sparkles size={16} className="text-brand-teal" />
              </div>
              <h2 className="text-sm font-semibold text-ink uppercase tracking-wider">Executive Plan Insight</h2>
              <span className="ml-auto text-[10px] font-bold text-brand-teal bg-brand-teal/5 px-2 py-0.5 rounded-md border border-brand-teal/20">
                DUVA AI
              </span>
            </div>

            {insightError && (
              <div className="flex flex-wrap items-center gap-2 mb-3">
                <p className="text-sm text-red-700">{insightError}</p>
                <button
                  type="button"
                  onClick={retryInsight}
                  className="text-sm font-medium text-brand-teal hover:underline"
                >
                  Retry
                </button>
              </div>
            )}
            {insightLoading ? (
              <div className="space-y-2">
                <div className="h-4 bg-warm-inset rounded animate-pulse w-full" />
                <div className="h-4 bg-warm-inset rounded animate-pulse w-5/6" />
              </div>
            ) : insight ? (
              <p className="text-base text-ink leading-relaxed font-medium">
                “{insight.insight}”
              </p>
            ) : insightError ? null : (
              <p className="text-sm text-ink-soft">
                AI analysis is pending more team activity. Check back soon for your weekly executive summary.
              </p>
            )}

            {insight?.generatedAt && (
              <p className="mt-4 text-[10px] text-ink-faint">
                Analysis based on metrics from the last 30 days · Updated {new Date(insight.generatedAt).toLocaleDateString()}
              </p>
            )}
          </div>
        </div>
      )}

      {summaryError && (
        <div
          className="mb-4 flex flex-wrap items-center gap-3 rounded-xl border px-4 py-3 text-sm"
          style={{ background: '#fdf2f2', borderColor: '#f5c2c2', color: '#991b1b' }}
        >
          <span>{summaryError}</span>
          <button type="button" onClick={retrySummary} className="font-semibold text-brand-teal hover:underline">
            Retry
          </button>
        </div>
      )}

      <section className="mb-8" aria-labelledby="home-org-activity">
        <div className="flex flex-wrap items-center justify-between gap-2 mb-4">
          <h2 id="home-org-activity" className="text-sm font-semibold text-ink">
            Your org activity
          </h2>
          <p className="text-xs text-ink-soft">Team-wide</p>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4 mb-4">
        <GlowMetricCard
          label="Engagement Score"
          glow="teal"
          breathe
          scoreProgress={score}
          icon={<TrendingUp size={18} className="text-brand-teal" />}
          loading={loading}
          tooltip="Blended 0–100 index from recent nudge activity—not the same as a single action %. Higher = more active habit building."
        >
          <span className={clsx('text-2xl font-bold tabular-nums', scoreColor)}>
            {score !== null ? score : '—'}
          </span>
          <span className="text-xs text-ink-soft ml-1">/100</span>
        </GlowMetricCard>
        <GlowMetricCard
          label="Nudges Sent"
          glow="sage"
          breathe
          icon={<Zap size={18} style={{ color: BRAND_SAGE }} />}
          loading={loading}
        >
          <span className="text-2xl font-bold text-ink tabular-nums">
            {summary?.weeklyStats?.sent ?? '—'}
          </span>
          <span className="text-xs text-ink-soft ml-1">this week</span>
        </GlowMetricCard>
        <GlowMetricCard
          label="Actions Taken"
          glow="teal"
          icon={<CheckCircle2 size={18} className="text-brand-teal" />}
          loading={loading}
        >
          <span className="text-2xl font-bold text-ink tabular-nums">
            {summary?.weeklyStats?.acted ?? '—'}
          </span>
          <span className="text-xs text-ink-soft ml-1">this week</span>
        </GlowMetricCard>
        <GlowMetricCard
          label="Behavioral ROI (Friction)"
          glow="teal"
          icon={<Zap size={18} className="text-brand-teal" />}
          loading={loading}
          tooltip="The percentage of nudges snoozed or skipped. Low friction indicates nudges are easy to act on."
        >
          <span className={clsx('text-2xl font-bold tabular-nums', (summary?.frictionScore ?? 0) > 30 ? 'text-red-700' : 'text-emerald-800')}>
            {summary?.frictionScore ?? 0}%
          </span>
          <span className="text-xs text-ink-soft ml-1">friction</span>
        </GlowMetricCard>
        <GlowMetricCard
          label="Active Members"
          glow="sage"
          icon={<Users size={18} style={{ color: BRAND_SAGE }} />}
          loading={loading}
        >
          <span className="text-2xl font-bold text-ink tabular-nums">
            {summary?.activeMemberCount ?? '—'}
          </span>
          <span className="text-xs text-ink-soft ml-1">receiving nudges</span>
        </GlowMetricCard>
        </div>

        {homeRoiLoading ? (
          <div
            className="rounded-2xl border p-6 shadow-card animate-pulse"
            style={{ background: WARM_CARD, borderColor: WARM_BORDER }}
          >
            <div className="h-4 bg-warm-inset rounded w-1/3 mb-4" />
            <div className="h-8 bg-warm-inset rounded w-2/3 mb-2" />
            <div className="h-4 bg-warm-inset rounded w-full mb-6" />
            <div className="h-10 bg-warm-inset rounded w-1/2" />
          </div>
        ) : homeRoi ? (
          <div
            className="rounded-2xl border p-5 shadow-card relative overflow-hidden"
            style={{
              background: 'linear-gradient(135deg, #EBF6FB 0%, #FDFCF8 100%)',
              borderColor: '#a7d8e0',
            }}
          >
            <div className="absolute top-0 right-0 p-3 opacity-10 pointer-events-none">
              <TrendingUp size={64} className="text-brand-teal" />
            </div>
            <div className="relative">
              <span className="inline-block bg-brand-teal/10 text-brand-teal text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full border border-brand-teal/20 mb-3">
                Behavioral ROI
              </span>
              <h3
                className="text-lg font-bold text-ink mb-1"
                style={{ fontFamily: "'Playfair Display', Georgia, serif" }}
              >
                {homeRoi.labels.headline}
              </h3>
              <p className="text-sm text-ink-soft mb-4">{homeRoi.labels.subline}</p>
              <div className="flex flex-wrap items-baseline gap-2 mb-5">
                <span className="text-3xl font-bold text-brand-teal tabular-nums">
                  ${homeRoi.estimatedValue.toLocaleString()}
                </span>
                <span className="text-xs text-ink-muted font-medium">estimated value created · last 30 days</span>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 pt-4 border-t border-brand-teal/20">
                <div>
                  <div className="flex items-center justify-between mb-2">
                    <p className="text-[11px] font-semibold text-ink-soft uppercase tracking-wide">Value multiplier</p>
                    <DollarSign size={14} className="text-emerald-600 shrink-0" />
                  </div>
                  <p className="text-2xl font-bold text-ink tabular-nums">
                    ${homeRoi.valuePerAction}
                    <span className="text-xs font-normal text-ink-muted ml-1">per completed action</span>
                  </p>
                </div>
                <div className="flex items-start gap-2">
                  <Info size={14} className="text-brand-teal shrink-0 mt-0.5" aria-hidden />
                  <p className="text-[11px] text-ink-soft leading-relaxed">
                    Illustrative planning assumption — not measured financial ROI for your organization.{' '}
                    <Link
                      to="/dashboard/behavioral-roi-methodology"
                      className="font-medium text-brand-teal hover:underline underline-offset-2"
                    >
                      Methodology &amp; citations
                    </Link>
                  </p>
                </div>
              </div>
            </div>
          </div>
        ) : null}

        {homePulseLoading ? (
          <div
            className="mt-4 rounded-2xl border p-5 animate-pulse"
            style={{ background: WARM_CARD, borderColor: WARM_BORDER }}
          >
            <div className="h-4 bg-warm-inset rounded w-1/2 mb-4" />
            <div className="h-24 bg-warm-inset rounded" />
          </div>
        ) : homePulse ? (
          <div
            className="mt-4 rounded-2xl border p-5 shadow-card"
            style={{ background: WARM_CARD, borderColor: WARM_BORDER }}
          >
            <div className="flex flex-wrap items-start justify-between gap-2 mb-4">
              <div>
                <h3 className="text-sm font-semibold text-ink">
                  Leadership Pulse (
                  {homePulse.monthLabel ??
                    new Date().toLocaleString('en-US', { month: 'long' })}
                  )
                </h3>
                <p className="text-[11px] text-ink-soft mt-0.5">
                  Aggregate 1-min Slack surveys · scores so far this calendar month
                </p>
              </div>
              {homePulse.stats && (
                <span className="text-[10px] bg-brand-teal/5 text-brand-teal px-2 py-0.5 rounded-md border border-brand-teal/10 font-medium shrink-0">
                  {homePulse.stats.response_count} responses
                </span>
              )}
            </div>
            {homePulse.isAnonymized ? (
              <div className="flex flex-col items-center justify-center text-center py-6 px-4">
                <Users size={28} className="text-ink-faint mb-3" />
                <p className="text-sm text-ink-muted font-medium">Anonymity guard active</p>
                <p className="text-xs text-ink-soft mt-1 max-w-sm">
                  Pulse results appear after at least 3 teammates respond this month. (Current:{' '}
                  {homePulse.count ?? 0}.)
                </p>
              </div>
            ) : homePulse.stats ? (
              <div className="space-y-3 max-w-lg">
                <HomePulseDimensionRow label="Inspiring others" score={homePulse.stats.inspiring} />
                <HomePulseDimensionRow label="Developing talent" score={homePulse.stats.developing} />
                <HomePulseDimensionRow label="Effective planning" score={homePulse.stats.planning} />
                <HomePulseDimensionRow label="Vision communication" score={homePulse.stats.vision} />
                <HomePulseDimensionRow label="Change management" score={homePulse.stats.change} />
              </div>
            ) : (
              <p className="text-sm text-ink-muted">No pulse responses for this calendar month yet.</p>
            )}
          </div>
        ) : null}
      </section>

      <EngagementScoreExplainer className="mb-8 -mt-4" />

      <div
        className="mb-8 rounded-2xl border p-6 shadow-card"
        style={{ background: WARM_CARD, borderColor: WARM_BORDER }}
      >
        <div className="flex flex-wrap items-start justify-between gap-2 mb-4">
          <div className="flex items-center gap-2">
            <Flame size={18} className="text-amber-700 shrink-0" />
            <h2 className="text-sm font-semibold text-ink">Your nudge activity</h2>
          </div>
          <p className="text-xs text-ink-soft">Your choices · rolling 7 days</p>
        </div>
        {progressError && (
          <div className="flex flex-wrap items-center gap-2 mb-3 text-sm">
            <span className="text-red-700">{progressError}</span>
            <button type="button" onClick={retryProgress} className="font-medium text-brand-teal hover:underline">
              Retry
            </button>
          </div>
        )}
        {progressLoading ? (
          <div className="h-[4.5rem] rounded-xl animate-pulse" style={{ background: '#f0ece4' }} />
        ) : myProgress ? (
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-5">
            <div>
              <p className="text-[11px] font-medium uppercase tracking-wide text-ink-muted mb-1">Streak</p>
              <div className="flex items-baseline gap-2 flex-wrap">
                <p className="text-2xl font-bold text-ink tabular-nums">
                  {myProgress.streakDays > 0 ? myProgress.streakDays : '—'}
                </p>
              </div>
              <p className="text-xs text-ink-soft mt-1 leading-snug">
                {myProgress.streakDays > 0
                  ? `${myProgress.streakDays === 1 ? 'day' : 'days'} in a row with at least one “Done”`
                  : 'Tap Done on a nudge to start a streak'}
              </p>
            </div>
            <div>
              <p className="text-[11px] font-medium uppercase tracking-wide text-ink-muted mb-1">Marked done</p>
              <p className="text-2xl font-bold text-ink tabular-nums">{myProgress.completedThisWeek}</p>
              <p className="text-xs text-ink-soft mt-1 leading-snug">This week</p>
            </div>
            <div>
              <div className="flex items-center justify-between mb-1">
                <p className="text-[11px] font-medium uppercase tracking-wide text-ink-muted">Badges & MVP</p>
                <span className="text-[10px] text-brand-teal font-bold">{myProgress.completedThisMonth} this month</span>
              </div>
              <div className="flex gap-2">
                <BadgeIcon 
                  icon={<Medal size={14} />} 
                  label="Silver" 
                  active={myProgress.completedThisMonth >= 4} 
                  color="text-slate-400"
                  tooltip="4+ actions/mo"
                />
                <BadgeIcon 
                  icon={<Award size={14} />} 
                  label="Gold" 
                  active={myProgress.completedThisMonth >= 8} 
                  color="text-amber-500"
                  tooltip="8+ actions/mo"
                />
                <BadgeIcon 
                  icon={<Trophy size={14} />} 
                  label="Diamond" 
                  active={myProgress.completedThisMonth >= 12} 
                  color="text-brand-teal"
                  tooltip="12+ actions/mo"
                />
              </div>
              <p className="text-[10px] text-ink-soft mt-2 leading-tight">
                {myProgress.completedThisMonth >= 12 
                  ? "Diamond MVP! You're a habit-building pro." 
                  : myProgress.completedThisMonth >= 8 
                    ? "Gold Nudger! 4 more to Diamond." 
                    : myProgress.completedThisMonth >= 4 
                      ? "Silver Nudger! 4 more to Gold." 
                      : `${4 - myProgress.completedThisMonth} more actions to reach Silver MVP.`}
              </p>
            </div>
          </div>
        ) : progressError ? null : (
          <p className="text-sm text-ink-muted">
            No personal stats yet. They appear after you use Done or Remind me later on a nudge.
          </p>
        )}
      </div>

      {/* Smart timezone prompt (non-admin, detected_tz available and mismatch or null setting) */}
      {user?.role !== 'admin' &&
        detectedTimezone &&
        !dismissedTzPrompt &&
        (meTimezone === null || (meTimezone !== detectedTimezone)) && (
          <div
            className="mb-8 rounded-2xl border p-5 shadow-card flex items-start gap-4 animate-fade-in relative overflow-hidden group"
            style={{ background: '#FDFCF8', borderColor: '#a7d8e0' }}
          >
            <div className="absolute top-2 right-2 opacity-0 group-hover:opacity-100 transition-opacity">
              <button
                onClick={dismissTzPrompt}
                className="p-1 hover:bg-ink/5 rounded-full text-ink-muted transition-colors"
                title="Dismiss"
              >
                <X size={16} />
              </button>
            </div>

            <div
              className="w-10 h-10 rounded-xl flex items-center justify-center shrink-0 shadow-inner"
              style={{ background: '#EBF6FB', color: '#2A7A8A' }}
            >
              <Shield size={20} className={meTimezone !== null ? 'animate-pulse' : ''} />
            </div>
            <div className="flex-1 min-w-0 pr-4">
              <p className="text-sm font-bold text-ink">
                {meTimezone === null ? 'When would you like your nudges?' : 'Traveling? You seem to be in a different timezone!'}
              </p>
              <p className="text-xs text-ink-muted mt-1 leading-relaxed">
                Found your location as <strong className="text-ink">{detectedTimezone.replace('_', ' ')}</strong>. 
                {meTimezone 
                  ? ` Your current setting is ${meTimezone.replace('_', ' ')}. Switching helps keep nudges accurate.`
                  : ' Setting this ensures your daily prompts arrive at the perfect local moment.'}
              </p>
              <div className="mt-4 flex items-center gap-3">
                <button
                  type="button"
                  onClick={() => void acceptDetectedTz()}
                  disabled={savingTz}
                  className="text-xs text-white font-bold px-5 py-2 rounded-full shadow-glow-teal disabled:opacity-60 transition-all active:scale-95"
                  style={{ background: '#2A7A8A' }}
                >
                  {savingTz ? 'Saving…' : 'Update Timezone ✓'}
                </button>
                <button
                  type="button"
                  onClick={dismissTzPrompt}
                  className="text-xs text-ink-soft hover:text-ink font-medium px-2 py-1 transition-colors"
                >
                  Skip for now
                </button>
              </div>
            </div>
          </div>
        )}

      <div
        id="personal-goal"
        className="mb-8 rounded-2xl border p-6 shadow-card scroll-mt-24"
        style={{ background: WARM_CARD, borderColor: WARM_BORDER }}
      >
        <div className="flex items-center gap-2 mb-3">
          <Sparkles size={18} className="text-brand-teal" />
          <h2 className="text-sm font-semibold text-ink">Personal AI Coach</h2>
        </div>
        <p className="text-sm text-ink-muted mb-4">
          What do you want to get better at this month? Our AI will personalize your nudges.
        </p>
        <form onSubmit={handleSetGoal} className="flex gap-3">
          <input
            type="text"
            value={goal}
            onChange={e => setGoal(e.target.value)}
            placeholder="e.g., I struggle to speak up in large meetings..."
            className="flex-1 rounded-xl px-4 py-2.5 text-sm text-ink outline-none transition-colors"
            style={{ background: '#f0ece4', border: `1px solid ${WARM_BORDER}` }}
          />
          <button
            type="submit"
            disabled={settingGoal || !goal.trim()}
            className="text-white font-medium text-sm px-5 py-2 rounded-full transition-colors disabled:opacity-50 whitespace-nowrap shadow-glow-teal"
            style={{ background: '#2A7A8A' }}
          >
            {settingGoal ? 'Thinking...' : 'Update tracking'}
          </button>
        </form>
        {track && (
          <div className="mt-4 rounded-xl border p-4" style={{ background: '#EBF6FB', borderColor: '#a7d8e0' }}>
            <div className="flex items-center gap-2 text-sm text-brand-teal font-semibold mb-2">
              <CheckCircle2 size={15} />
              You're on the <span className="capitalize">{track}</span> track
            </div>
            {trackExamples.length > 0 && (
              <div>
                <p className="text-xs text-ink-muted mb-2 flex items-center gap-1">
                  <Lightbulb size={11} /> You'll receive nudges like:
                </p>
                <ul className="space-y-1.5">
                  {trackExamples.map((ex, i) => (
                    <li key={i} className="text-xs text-ink flex items-start gap-1.5">
                      <span className="text-brand-teal shrink-0 mt-0.5">›</span>
                      <span>"{ex}"</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        )}
      </div>


      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-6">
        <div
          className="lg:col-span-2 rounded-2xl border p-6 shadow-card"
          style={{ background: WARM_CARD, borderColor: WARM_BORDER }}
        >
          <h2 className="text-sm font-semibold text-ink mb-4">Engagement Score — Last 30 Days</h2>
          {summary?.isAnonymized ? (
            <div className="h-[200px] flex flex-col items-center justify-center text-center px-4">
              <Users size={32} className="text-ink-faint mb-3" />
              <p className="text-sm text-ink-muted font-medium">Anonymity Guard Active</p>
              <p className="text-xs text-ink-soft mt-1">Detailed trends are hidden for teams with fewer than 3 members to protect individual privacy.</p>
            </div>
          ) : trendError ? (
            <div className="h-[200px] flex flex-col items-center justify-center gap-2 text-center px-4">
              <p className="text-sm text-red-700">{trendError}</p>
              <button type="button" onClick={retryTrend} className="text-sm font-medium text-brand-teal hover:underline">
                Retry
              </button>
            </div>
          ) : chartData.length > 0 ? (
            <ResponsiveContainer width="100%" height={200}>
              <LineChart data={chartData}>
                <XAxis
                  dataKey="date"
                  tick={{ fontSize: 11, fill: INK }}
                  tickLine={false}
                  axisLine={{ stroke: WARM_BORDER }}
                />
                <YAxis
                  domain={[0, 100]}
                  tick={{ fontSize: 11, fill: INK }}
                  tickLine={false}
                  axisLine={{ stroke: WARM_BORDER }}
                  width={32}
                />
                <Tooltip
                  content={({ active, payload, label }) => {
                    if (!active || !payload?.length) return null
                    return (
                      <div
                        className="rounded-lg px-3 py-2 text-xs shadow-card border"
                        style={{ background: WARM_CARD, borderColor: WARM_BORDER }}
                      >
                        <p className="text-ink font-medium mb-0.5">{String(label)}</p>
                        <p className="text-brand-teal font-semibold tabular-nums">
                          {(payload[0].value as number)} / 100
                        </p>
                      </div>
                    )
                  }}
                />
                <Line
                  type="monotone"
                  dataKey="score"
                  stroke={BRAND_TEAL}
                  strokeWidth={3}
                  strokeLinejoin="round"
                  strokeLinecap="round"
                  dot={false}
                  activeDot={{ r: 5, fill: BRAND_TEAL, stroke: WARM_CARD, strokeWidth: 2 }}
                />
              </LineChart>
            </ResponsiveContainer>
          ) : (
            <EmptyState message="Engagement data will appear here once nudges start being delivered." />
          )}
        </div>

        {mabError && !summary?.isAnonymized && (
          <div
            className="rounded-2xl border p-6 shadow-card"
            style={{ background: WARM_CARD, borderColor: WARM_BORDER }}
          >
            <h2 className="text-sm font-semibold text-ink mb-2">Winning Tracks</h2>
            <p className="text-sm text-red-700 mb-2">{mabError}</p>
            <button type="button" onClick={retryMab} className="text-sm font-medium text-brand-teal hover:underline">
              Retry
            </button>
          </div>
        )}
        {mabStats.length > 0 && !summary?.isAnonymized && !mabError && (
          <div
            className="rounded-2xl border p-6 shadow-card"
            style={{ background: WARM_CARD, borderColor: WARM_BORDER }}
          >
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-sm font-semibold text-ink">Winning Tracks</h2>
              <Sparkles size={16} className="text-brand-teal" />
            </div>
            <div className="space-y-4">
              {mabStats.slice(0, 3).map((stat, i) => (
                <div key={stat.category} className="relative">
                  <div className="flex justify-between text-xs mb-1.5">
                    <span className="capitalize font-medium text-ink-muted">{stat.category}</span>
                    <span className="text-brand-teal font-bold">{stat.conversion_rate}%</span>
                  </div>
                  <div className="h-1.5 w-full bg-warm-inset rounded-full overflow-hidden">
                    <div 
                      className={clsx("h-full rounded-full transition-all duration-1000", i === 0 ? "bg-brand-teal" : "bg-brand-sage")} 
                      style={{ width: `${stat.conversion_rate}%` }} 
                    />
                  </div>
                </div>
              ))}
            </div>
            <p className="mt-5 text-[10px] text-ink-soft leading-tight">
              Calculated via Multi-Armed Bandit (MAB) selection based on your team&apos;s real-time interaction patterns.
            </p>
          </div>
        )}
        <BuddyWidget
          pair={myPair}
          userId={user?.id ?? ''}
          preferredChannel={buddyMeContext?.preferred_channel}
          slackTeamId={buddyMeContext?.slack_team_id ?? null}
          loading={buddyLoading}
          error={buddyError}
          onRetry={retryBuddy}
        />
      </div>

      {riskFlags.length > 0 && user?.role === 'admin' && (
        <div
          className="rounded-2xl border p-5"
          style={{ background: '#fdf8f0', borderColor: '#e8d4b8' }}
        >
          <div className="flex items-center gap-2 mb-3">
            <AlertTriangle size={16} className="text-amber-700" />
            <h2 className="text-sm font-semibold text-ink">Things to watch</h2>
          </div>
          <ul className="space-y-3 list-none pl-0 m-0">
            {riskFlags.map((flag) => {
              const { title, detail } = getRiskFlagCopy(flag)
              return (
                <li key={flag} className="text-sm text-amber-950">
                  <span className="font-medium text-ink">{title}</span>
                  {detail ? (
                    <p className="mt-1 text-xs text-ink-muted leading-relaxed max-w-2xl">{detail}</p>
                  ) : null}
                </li>
              )
            })}
          </ul>
        </div>
      )}

      {myProgress && (
        <div className="mb-8">
          <StreakShield 
            available={shieldAvailable === true} 
            usedThisMonth={shieldUsed} 
            streakDays={myProgress.streakDays}
            onUsed={() => {
              setShieldAvailable(false)
              setShieldUsed(true)
              // Re-fetch progress to update the streak immediately
              retryProgress()
            }}
          />
        </div>
      )}
    </div>
  )
}

function GlowMetricCard({
  label,
  icon,
  children,
  loading,
  glow,
  breathe,
  scoreProgress,
  tooltip,
}: {
  label: string
  icon: React.ReactNode
  children: React.ReactNode
  loading?: boolean
  glow: 'teal' | 'sage'
  breathe?: boolean
  scoreProgress?: number | null
  tooltip?: string
}) {
  const glowClass = glow === 'teal' ? 'glow-radial-teal' : 'glow-radial-sage'
  const glowOpacity =
    glow === 'teal' && scoreProgress != null
      ? 0.55 + (Math.min(100, Math.max(0, scoreProgress)) / 100) * 0.45
      : 1

  return (
    <div
      className={clsx(
        'relative overflow-hidden rounded-2xl border p-5 shadow-card',
        breathe && 'animate-breathe'
      )}
      style={{ background: WARM_CARD, borderColor: WARM_BORDER }}
    >
      <div
        className={clsx('absolute inset-0 pointer-events-none', glowClass)}
        style={{ opacity: glowOpacity }}
        aria-hidden
      />
      <div className="relative">
        <div className="flex items-center justify-between mb-3">
          <div className="flex flex-col">
            <span className="text-xs font-medium text-ink-soft">{label}</span>
            {tooltip && <span className="text-[10px] text-ink-faint leading-tight mt-0.5 max-w-[120px]">{tooltip}</span>}
          </div>
          {icon}
        </div>
        {loading ? (
          <div className="h-8 w-20 rounded animate-pulse bg-warm-inset" />
        ) : (
          <div className="flex items-baseline gap-1 flex-wrap">{children}</div>
        )}
      </div>
    </div>
  )
}

function BuddyWidget({
  pair,
  userId,
  preferredChannel,
  slackTeamId,
  loading,
  error,
  onRetry,
}: {
  pair: AccountabilityPair | null
  userId: string
  preferredChannel: string | undefined
  slackTeamId: string | null
  loading: boolean
  error: string | null
  onRetry: () => void
}) {
  const buddyName = pair
    ? (pair.user_a_id === userId ? pair.user_b_name : pair.user_a_name)
    : null
  const buddyEmail = pair
    ? (pair.user_a_id === userId ? pair.user_b_email : pair.user_a_email)
    : null
  const initial = (buddyName ?? buddyEmail ?? '?')[0]?.toUpperCase()

  const trackLabel = pair?.nudge_track
    ? pair.nudge_track.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase())
    : null

  return (
    <div
      className="flex flex-col rounded-2xl border p-6 shadow-card"
      style={{ background: WARM_CARD, borderColor: WARM_BORDER }}
    >
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-sm font-semibold text-ink">Accountability Buddy</h2>
        <Link to="/dashboard/accountability" className="text-xs text-brand-teal hover:text-brand-teal-hover flex items-center gap-1 font-medium">
          View all <ArrowRight size={11} />
        </Link>
      </div>

      {error && !loading ? (
        <div className="flex-1 flex flex-col items-center justify-center text-center gap-2 py-4">
          <p className="text-xs text-red-700">{error}</p>
          <button type="button" onClick={onRetry} className="text-xs font-medium text-brand-teal hover:underline">
            Retry
          </button>
        </div>
      ) : loading ? (
        <div className="flex-1 flex flex-col gap-3 animate-pulse">
          <div className="h-12 w-12 rounded-full bg-warm-inset mx-auto" />
          <div className="h-4 bg-warm-inset rounded w-3/4 mx-auto" />
          <div className="h-3 bg-warm-inset rounded w-1/2 mx-auto" />
        </div>
      ) : pair ? (
        <div className="flex-1 flex flex-col items-center text-center relative">
          <div
            className="absolute inset-x-0 top-0 h-32 pointer-events-none glow-radial-teal opacity-50 rounded-full"
            aria-hidden
          />
          <div className="relative w-14 h-14 rounded-full bg-brand-teal flex items-center justify-center text-xl font-bold text-white mb-3 shadow-glow-teal">
            {initial}
          </div>
          <p className="text-sm font-semibold text-ink leading-tight">{buddyName ?? buddyEmail}</p>
          {buddyEmail && buddyName && (
            <p className="text-xs text-ink-soft mt-1 truncate max-w-full">{buddyEmail}</p>
          )}
          {trackLabel && (
            <span
              className="mt-3 text-xs px-2.5 py-0.5 rounded-full border font-medium"
              style={{ background: '#EBF6FB', color: BRAND_TEAL, borderColor: '#a7d8e0' }}
            >
              {trackLabel} track
            </span>
          )}
          {buddyEmail &&
            (() => {
              const buddySlackId = pair.user_a_id === userId ? pair.user_b_slack_id : pair.user_a_slack_id
              const buddyTeamsId = pair.user_a_id === userId ? pair.user_b_teams_id : pair.user_a_teams_id
              const action = resolveBuddyCheckInAction({
                preferredChannel,
                slackTeamId,
                buddySlackId: buddySlackId ?? undefined,
                buddyTeamsId: buddyTeamsId ?? undefined,
                buddyEmail,
              })
              if (!action) return null
              return (
                <a
                  href={action.href}
                  {...(action.external ? { target: '_blank', rel: 'noreferrer' as const } : {})}
                  className="mt-4 w-full text-center text-xs font-medium px-3 py-2 rounded-lg transition-colors border text-brand-teal hover:bg-warm-inset"
                  style={{ borderColor: WARM_BORDER, background: WARM_CARD }}
                >
                  {action.label} →
                </a>
              )
            })()}
        </div>
      ) : (
        <div className="flex-1 flex flex-col items-center justify-center text-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-warm-inset flex items-center justify-center border" style={{ borderColor: WARM_BORDER }}>
            <Users2 size={18} className="text-ink-soft" />
          </div>
          <p className="text-xs text-ink-muted leading-relaxed">
            No buddy yet. Pairs are created every Monday at 8am.
          </p>
          <Link
            to="/dashboard/accountability"
            className="text-xs text-brand-teal hover:text-brand-teal-hover underline underline-offset-2 font-medium"
          >
            Learn more
          </Link>
        </div>
      )}
    </div>
  )
}

function EmptyState({ message }: { message: string }) {
  return (
    <div className="h-[200px] flex items-center justify-center">
      <p className="text-sm text-ink-muted text-center max-w-xs">{message}</p>
    </div>
  )
}

function HomePulseDimensionRow({ label, score }: { label: string; score: number }) {
  return (
    <div>
      <div className="flex justify-between text-[11px] mb-1">
        <span className="font-medium text-ink-muted">{label}</span>
        <span className="text-brand-teal font-bold tabular-nums">{score} / 5</span>
      </div>
      <div className="h-1.5 w-full bg-warm-inset rounded-full overflow-hidden">
        <div
          className="h-full bg-brand-teal rounded-full transition-all duration-1000"
          style={{ width: `${(score / 5) * 100}%` }}
        />
      </div>
    </div>
  )
}

function BadgeIcon({ icon, label, active, color, tooltip }: { 
  icon: React.ReactNode; 
  label: string; 
  active: boolean; 
  color: string;
  tooltip: string;
}) {
  return (
    <div className="group relative">
      <div 
        className={clsx(
          "w-8 h-8 rounded-full flex items-center justify-center transition-all duration-500 border",
          active 
            ? `${color} bg-warm-card border-brand-teal/30 shadow-sm scale-110` 
            : "text-ink-faint bg-warm-inset border-warm-border opacity-40"
        )}
        title={active ? `${label} Badge: ${tooltip}` : `Locked: ${tooltip}`}
      >
        {icon}
      </div>
      <div className="absolute -bottom-1 -right-1">
        {active && <div className="w-2.5 h-2.5 bg-emerald-500 rounded-full border-2 border-warm-card shadow-sm" />}
      </div>
    </div>
  )
}

function getTimeOfDay(): string {
  const h = new Date().getHours()
  if (h < 12) return 'morning'
  if (h < 17) return 'afternoon'
  return 'evening'
}

